import {
  Notification,
  ScheduledNotification,
} from "@pimo/notifications-plugin-v2";
import type { SettingsCommit, SettingsCommitResult } from "product-test-types";
import {
  BasicItem,
  Configuration,
  Profile,
  Project,
  SettingsResponse,
  SettingsSeatInfo,
  SettingsUsersResponse,
} from "product-test-types";
import { getDefaultConfig } from "product-test-utils";

import { STRAPI_URL } from "../env";

function stripReportingItemsFromProjects(projects: Project[]): Project[] {
  return (projects ?? []).map((project) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { reportingItems: _reportingItems, ...rest } = project;
    return rest as Project;
  });
}

function stripReportingItemsFromBasicItems(
  basicItems: BasicItem[]
): BasicItem[] {
  return (basicItems ?? []).map((basicItem) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { reportingItems: _reportingItems, ...rest } = basicItem;
    return rest as BasicItem;
  });
}

function sanitizeSettingsCommit(commit: SettingsCommit): SettingsCommit {
  return {
    ...commit,
    projects: commit.projects
      ? {
          ...commit.projects,
          upsert: stripReportingItemsFromProjects(commit.projects.upsert ?? []),
        }
      : undefined,
    basicItems: commit.basicItems
      ? {
          ...commit.basicItems,
          upsert: stripReportingItemsFromBasicItems(
            commit.basicItems.upsert ?? []
          ),
        }
      : undefined,
  };
}

export type SeatInfo = SettingsSeatInfo;

export async function fetchSettings(
  language: string = "en"
): Promise<SettingsResponse> {
  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings`, {
      credentials: "include",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    const json = (await response.json()) as SettingsResponse;

    return {
      ...json,
      configuration: json.configuration,
    };
  } catch {
    // toolUsers/clientUsers are intentionally omitted (left undefined) here
    // rather than defaulted to []: an empty array reads downstream as
    // "confirmed there are zero users", and a subsequent publish/commit
    // would send that as an explicit roster, causing the backend to sweep
    // every real user's group membership for this tool/client. Leaving them
    // undefined preserves the "we don't know, don't touch users" semantics
    // relied on by saveSettings/commit's hasExplicitToolUsers check.
    return {
      projects: [],
      basicItems: [],
      seatInfo: undefined,
      mfaPolicy: "off",
      configuration: getDefaultConfig(language as "en" | "de"),
    };
  }
}

export async function updateSettings(params: {
  projects: Project[];
  basicItems: BasicItem[];
  trackingItemsApplyToAllProjects: boolean;
  configuration: Configuration;
  toolUsers?: Profile[];
  clientUsers?: Profile[];
  hasUpdatedUsers?: boolean;
  invitationLanguage?: "en" | "de";
  notificationSettings: {
    notifications: Notification[];
    scheduledNotifications: ScheduledNotification[];
    published: boolean;
  };
}): Promise<
  | {
      basicItems: BasicItem[];
      configuration: Configuration;
      projects: Project[];
      toolUsers?: Profile[];
      clientUsers?: Profile[];
      seatInfo?: SeatInfo | null;
      notifications: Notification[];
      scheduledNotifications: ScheduledNotification[];
      completedSetupSteps?: {
        hasGeneralData: boolean;
        hasReportingData: boolean;
        hasProjectsAndDeliverables: boolean;
        hasInvitedUsers: boolean;
        hasNotifications: boolean;
      };
    }
  | { validationErrors: Record<string, unknown> }
  | undefined
> {
  const {
    basicItems,
    trackingItemsApplyToAllProjects,
    projects,
    configuration,
    toolUsers,
    clientUsers,
    hasUpdatedUsers,
    invitationLanguage,
    notificationSettings: { notifications, scheduledNotifications, published },
  } = params;
  const projectsForSettingsUpdate = stripReportingItemsFromProjects(projects);
  const basicItemsForSettingsUpdate =
    stripReportingItemsFromBasicItems(basicItems);

  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projects: projectsForSettingsUpdate,
        basicItems: basicItemsForSettingsUpdate,
        trackingItemsApplyToAllProjects,
        configuration: configuration,
        clientUsers: clientUsers,
        toolUsers: toolUsers,
        notificationSettings: {
          notifications,
          scheduledNotifications,
          published,
        },

        hasUpdatedUsers,
        invitationLanguage,
      }),
    });

    // Validation failures (e.g. duplicate users, seat-limit exceeded) come back
    // as 422 with a body of shape { message, errors }. Surface those field
    // errors so the form can render them inline instead of silently failing.
    if (response.status === 422) {
      const body = (await response.json()) as {
        errors?: Record<string, unknown>;
      };
      return { validationErrors: body.errors ?? {} };
    }

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    const json = (await response.json()) as {
      projects: Project[];
      basicItems: BasicItem[];
      configuration: Configuration;
      toolUsers?: Profile[];
      clientUsers?: Profile[];
      seatInfo?: SeatInfo | null;
      notifications: Notification[];
      scheduledNotifications: ScheduledNotification[];
      completedSetupSteps?: {
        hasGeneralData: boolean;
        hasReportingData: boolean;
        hasProjectsAndDeliverables: boolean;
        hasInvitedUsers: boolean;
        hasNotifications: boolean;
      };
    };
    return json;
  } catch {
    return;
  }
}

export async function clearSettings(params: { users?: Profile[] }): Promise<
  | {
      basicItems: BasicItem[];
      configuration: Configuration;
      projects: Project[];
      users?: Profile[];
    }
  | undefined
> {
  const { users } = params;

  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings/clear`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        users,
      }),
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    const json = (await response.json()) as {
      projects: Project[];
      basicItems: BasicItem[];
      configuration: Configuration;
      users?: Profile[];
    };
    return json;
  } catch {
    return;
  }
}

export async function fetchSetupSteps(): Promise<
  | {
      hasGeneralData: boolean;
      hasReportingData: boolean;
      hasProjectsAndDeliverables: boolean;
      hasInvitedUsers: boolean;
      hasNotifications: boolean;
    }
  | undefined
> {
  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings/setup-steps`, {
      credentials: "include",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    const json = (await response.json()) as {
      hasGeneralData: boolean;
      hasReportingData: boolean;
      hasProjectsAndDeliverables: boolean;
      hasInvitedUsers: boolean;
      hasNotifications: boolean;
    };
    return json;
  } catch {
    return undefined;
  }
}

export async function fetchUsers(): Promise<SettingsUsersResponse | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings/users`, {
      credentials: "include",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    const json = (await response.json()) as SettingsUsersResponse;
    return json;
  } catch {
    return undefined;
  }
}

export async function commitSettings(
  commit: SettingsCommit
): Promise<
  | SettingsCommitResult
  | { validationErrors: Record<string, unknown> }
  | undefined
> {
  try {
    const sanitizedCommit = sanitizeSettingsCommit(commit);
    const response = await fetch(`${STRAPI_URL}/bff-settings/commit`, {
      credentials: "include",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sanitizedCommit),
    });

    if (response.status === 422) {
      const body = (await response.json()) as {
        errors?: Record<string, unknown>;
      };
      return { validationErrors: body.errors ?? {} };
    }

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    return (await response.json()) as SettingsCommitResult;
  } catch {
    return undefined;
  }
}
